import React, { useEffect } from 'react';
import { useCityContext } from '../../context/CityContext';
import CityCard from '../../components/CityCard';
import './Landing.css';

export default function Landing() {
  const { cityList, deactivateCity } = useCityContext();

  useEffect(() => {
    deactivateCity();
    document.title = 'Notify Cities — Alerts for Every City';
  }, [deactivateCity]);

  return (
    <div className="landing">
      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-hero-eyebrow">🔔 Citizen Notification Platform</div>
          <h1 className="landing-hero-title">
            One platform.<br />Every city.
          </h1>
          <p className="landing-hero-sub">
            A scalable, fully branded notification portal cities can deploy in minutes. Residents sign up for the alerts that matter to them — street sweeping, emergencies, utility outages, and more.
          </p>
          <div className="landing-hero-chips">
            <span className="landing-chip">🎨 Custom branding per city</span>
            <span className="landing-chip">📬 Email, SMS & Push</span>
            <span className="landing-chip">⚙ No-code Admin Config</span>
          </div>
        </div>
        <div className="landing-hero-graphic">
          <div className="hero-graphic-ring ring-1" />
          <div className="hero-graphic-ring ring-2" />
          <div className="hero-graphic-ring ring-3" />
          <div className="hero-graphic-center">🔔</div>
        </div>
      </section>

      {/* City grid */}
      <section className="landing-cities">
        <div className="landing-cities-inner">
          <div className="landing-section-header">
            <h2 className="landing-section-title">Select your city</h2>
            <p className="landing-section-sub">
              Each portal is independently branded and configured — try switching between cities to see the platform adapt.
            </p>
          </div>
          <div className="landing-cities-grid">
            {cityList.map((city) => (
              <CityCard key={city.slug} city={city} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="landing-how">
        <div className="landing-how-inner">
          <h2 className="landing-section-title">How it works</h2>
          <div className="landing-steps">
            {[
              { icon: '🏙️', step: '01', title: 'Choose your city', desc: 'Each city has its own branded portal with relevant notification topics.' },
              { icon: '📋', step: '02', title: 'Browse & select alerts', desc: 'Pick the notification categories that matter to you — parking, emergencies, events, and more.' },
              { icon: '📬', step: '03', title: 'Enter your contact info', desc: 'Choose how you\'d like to be notified — email, SMS, or push notifications.' },
              { icon: '✅', step: '04', title: 'Stay informed', desc: 'Receive timely, relevant alerts from your city directly to your preferred channel.' },
            ].map((s) => (
              <div key={s.step} className="landing-step">
                <div className="landing-step-number">{s.step}</div>
                <div className="landing-step-icon">{s.icon}</div>
                <h3 className="landing-step-title">{s.title}</h3>
                <p className="landing-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin CTA */}
      <section className="landing-admin-cta">
        <div className="landing-admin-cta-inner">
          <div className="landing-admin-cta-icon">⚙</div>
          <h2>Adding a new city is one config file</h2>
          <p>
            Each city is defined by a single JavaScript config object — name, colors, logo, topics. Zero code changes needed elsewhere. The Admin Panel lets you preview and edit themes in real time.
          </p>
          <a href="/admin" className="cta-button">Open Admin Panel →</a>
        </div>
      </section>
    </div>
  );
}
